/**
 * Emergency contacts — two tabs: All contacts (danger-tinted cards, SOS
 * auto-call note) and Security (gate guards, callable directly — MyGate
 * parity). Wired: GET /api/account/emergency-contacts ({items, guards}).
 * Port of screens-account.jsx EmergencyContacts.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, extractItems, str } from '@/components/gate/api';
import { InfoNote, Segmented } from '@/components/gate/bits';
import { CallIconButton, goBack, IconCircle } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Pill, Screen, Skeleton, TopBar } from '@/ui';

type Contact = { id: string; name: string; rel: string; phone?: string; primary: boolean };

function toContact(c: unknown, i: number): Contact {
  return {
    id: str(c, 'id') ?? `e${i}`,
    name: str(c, 'name', 'fullName', 'label') ?? 'Contact',
    rel: str(c, 'rel', 'relation', 'relationship') ?? '',
    phone: str(c, 'phone', 'phoneNumber'),
    primary: bool(c, 'primary', 'isPrimary') ?? false,
  };
}

export default function EmergencyContactsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [rows, setRows] = React.useState<Contact[]>([]);
  const [guards, setGuards] = React.useState<Contact[]>([]);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [tab, setTab] = React.useState('All contacts');

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<unknown>('/api/account/emergency-contacts');
        if (cancelled) return;
        setRows(extractItems<unknown>(body, 'contacts', 'emergency').map(toContact));
        const g = (body && typeof body === 'object' && 'guards' in body)
          ? (body as { guards: unknown[] }).guards : [];
        setGuards((Array.isArray(g) ? g : []).map(toContact));
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Screen scroll={false}>
      <TopBar title={t('em_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ marginBottom: space.s4 }}>
          <Segmented options={['All contacts', 'Security']} labels={[t('em_all_contacts'), t('em_security')]} value={tab} onChange={setTab} />
        </View>

        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} h={76} />)}
          </View>
        ) : tab === 'Security' ? (
          guards.length === 0 ? (
            <EmptyState
              icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
              title={t('em_no_guards')}
              body={t('em_no_guards_body')}
            />
          ) : (
            <>
              <InfoNote icon="shield" tint={colors.primary500} style={{ marginBottom: space.s4 }}>
                {t('em_guard_note')}
              </InfoNote>
              <View style={{ gap: space.s3 }}>
                {guards.map((c) => (
                  <Card key={c.id}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                      <IconCircle name="shield" size={44} bg={colors.primary100} fg={colors.primary700} />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <AppText variant="bodyMd" weight="500">{c.name}</AppText>
                        <AppText variant="bodySm" tone="tertiary" mono style={{ marginTop: 2 }}>{c.phone ?? '—'}</AppText>
                      </View>
                      <CallIconButton phone={c.phone} name={c.name} prominent />
                    </View>
                  </Card>
                ))}
              </View>
            </>
          )
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="phone" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('em_err_title') : t('em_empty_title')}
            body={state === 'error' ? t('em_err_body') : t('em_empty_body')}
          />
        ) : (
          <>
            <InfoNote icon="shield" tint={colors.primary500} style={{ marginBottom: space.s4 }}>
              {t('em_sos_note')}
            </InfoNote>
            <View style={{ gap: space.s3 }}>
              {rows.map((c) => (
                <Card key={c.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <IconCircle name="phone" size={44} bg={colors.danger50} fg={colors.danger500} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                        <AppText variant="bodyMd" weight="500">{c.name}</AppText>
                        {c.primary ? <Pill status="urgent" label={t('pill_primary')} /> : null}
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                        {c.rel ? <AppText variant="bodySm" tone="tertiary">{c.rel} ·</AppText> : null}
                        <AppText variant="bodySm" tone="tertiary" mono>{c.phone ?? '—'}</AppText>
                      </View>
                    </View>
                    <CallIconButton phone={c.phone} name={c.name} prominent />
                  </View>
                </Card>
              ))}
            </View>
          </>
        )}
      </Screen>
    </Screen>
  );
}
